/* Account (حسابي) — full-screen account hub with portal-fixed header,
   matching the MobileChat aesthetic. Slim orchestrator — cards live
   under ./account/*. */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ChartLineUp, Crown, Devices, Envelope, Fingerprint, Gear,
  Info, Key, Lightning, Scroll, SignOut, Trash, Trophy,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { useUser } from "../context/UserAuthContext";
import { useAdmin } from "../context/AdminAuthContext";
import ChangePasswordSheet from "../components/account/ChangePasswordSheet";
import DeleteAccountSheet from "../components/account/DeleteAccountSheet";
import BiometricSheet from "../components/account/BiometricSheet";
import SessionsSheet from "../components/account/SessionsSheet";
import BarCardScanner from "../components/signup/BarCardScanner";
import MyLawyerCard from "../components/lawyers/MyLawyerCard";
import { renewBarCard } from "../lib/userApi";
import LexiqWordmark from "../components/LexiqWordmark";
import { isIOSNative } from "../lib/platform";
import MobileGlassHeader, {
  MobilePageBackground, MOBILE_HEADER_CONTENT_OFFSET,
} from "../components/MobileGlassHeader";

import AdminFeatureCard from "./account/AdminFeatureCard";
import BarCardSection from "./account/BarCardSection";
import { Group, Row, SectionTitle } from "./account/_shared";

export default function Account() {
  const { user, logout, refresh } = useUser();
  const { admin } = useAdmin();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [pwdOpen, setPwdOpen] = useState(false);
  const [bioOpen, setBioOpen] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewing, setRenewing] = useState(false);

  const handleRenewComplete = async (data) => {
    setRenewing(true);
    try {
      await renewBarCard({
        bar_id_number: data.bar_id_number,
        bar_barcode: data.bar_barcode,
        issue_date: data.bar_issue_date,
        validity_grade: data.bar_validity_grade,
        validity_until: data.bar_validity_until,
        date_of_birth: data.date_of_birth,
      });
      toast.success("تَمَّ تَجديد بِطاقة النِّقابة بِنَجاح.");
      await refresh?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "تعذّر تَجديد البِطاقة.";
      toast.error(msg);
    } finally {
      setRenewing(false);
    }
  };

  // Paint body + html + iOS theme-color identical to the gradient top so
  // the device dead-zone (notch / status bar) is filled by the page colour.
  useEffect(() => {
    const prevBodyBg = document.body.style.backgroundColor;
    const prevHtmlBg = document.documentElement.style.backgroundColor;
    document.body.style.backgroundColor = "#FFFFFF";
    document.documentElement.style.backgroundColor = "#FFFFFF";
    const meta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", "#FFFFFF");
    return () => {
      document.body.style.backgroundColor = prevBodyBg;
      document.documentElement.style.backgroundColor = prevHtmlBg;
      if (meta && prevTheme !== null) meta.setAttribute("content", prevTheme);
    };
  }, []);

  // Deep-link from /lawyers/iraq → scroll to public-presence section.
  useEffect(() => {
    if (sp.get("focus") !== "public-presence" || !user) return;
    const id = setTimeout(() => {
      const el = document.getElementById("public-presence");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.style.transition = "box-shadow .4s ease";
        el.style.boxShadow = "0 0 0 3px rgba(212,175,55,0.45)";
        setTimeout(() => { el.style.boxShadow = ""; }, 2200);
      }
    }, 250);
    return () => clearTimeout(id);
  }, [sp, user]);

  const onLogout = async () => { await logout(); nav("/login", { replace: true }); };

  return (
    <>
      <MobilePageBackground />
      <MobileGlassHeader
        title="حسابي"
        subtitle="إدارة الملف الشخصي"
        backTo="/"
        rightSlot={
          <Link
            to="/settings" data-testid="account-header-settings" aria-label="الإعدادات"
            className="w-10 h-10 rounded-full bg-white shadow-[0_2px_8px_rgba(10,25,47,0.10)] flex items-center justify-center active:scale-95 transition-transform"
          >
            <Gear size={18} weight="bold" className="text-[#0A192F]" />
          </Link>
        }
        testid="mobile-account-header"
      />
      <div
        className="lg:hidden min-h-[100dvh] px-4 pb-6 font-tajawal"
        style={{ paddingTop: MOBILE_HEADER_CONTENT_OFFSET }}
        data-testid="page-account"
      >
        {user && user.role !== "client" && <MyLawyerCard user={user} />}
        {user && user.role !== "client" && (
          <BarCardSection user={user} renewing={renewing} onOpenRenew={() => setRenewOpen(true)} />
        )}

        {/* Admin card NEVER renders unless we have BOTH an authenticated
            user AND a confirmed admin session (AdminAuthContext enforces
            that pairing at the token level). */}
        {user && admin && <AdminFeatureCard />}

        {user && user.role !== "client" && (
          <>
            <SectionTitle>رحلتي مع LexIQ</SectionTitle>
            <Group>
              <Row to="/account/subscription" icon={Lightning} label="اِشتِراكي"
                   hint="باقَتي الحاليّة وَاِستِخدامي الشَّهري + التَّرقية"
                   testid="account-subscription-link" />
              <Row to="/account/stats" icon={ChartLineUp} label="إحصائيّات قَضيّتي"
                   hint="نَشاطك على المَراحِل + أَكثَر القَضايا نَشاطاً + مُعَدَّل المُشارَكَة"
                   testid="account-mystats-link" />
              <Row to="/achievements" icon={Trophy} label="إنجازاتي"
                   hint="سجلّ المعالم التي حقّقتها — موكِّلون، وثائق، أسئلة"
                   testid="account-achievements-link" />
              <Row to="/leaderboard" icon={Crown} label="جَدول الترتيب"
                   hint="رتبتك بين المحامين المُفَعَّلين في LEXIQ"
                   testid="account-leaderboard-link" />
            </Group>
          </>
        )}

        {user && user.role === "client" && (
          <>
            <SectionTitle>اِشتِراكي</SectionTitle>
            <Group>
              <Row to="/account/subscription" icon={Lightning} label="باقَتي وَاِستِخدامي"
                   hint="باقَتي الحاليّة وَاِستِخدامي الشَّهري + التَّرقية"
                   testid="account-subscription-link" />
              {!isIOSNative() && (
                <Row to="/pricing" icon={Crown} label="مُشاهَدة الباقات"
                     hint="اِشتَرِك بِأَيّ باقَة لِفَتح مَيزات الذَّكاء الاِصطِناعي القانونيّ"
                     testid="account-pricing-link" />
              )}
            </Group>
          </>
        )}

        {user && (
          <>
            <SectionTitle>الأَمان</SectionTitle>
            <Group>
              <Row onClick={() => setBioOpen(true)} icon={Fingerprint} label="الدُّخول بِالبَصمَة"
                   hint="Face ID / Touch ID / Windows Hello — بِلا كَلِمَة سِرّ"
                   testid="account-biometric-link" />
              <Row onClick={() => setSessionsOpen(true)} icon={Devices} label="الأَجهِزَة النَّشِطَة"
                   hint="شاهِد كُلّ الأَجهِزَة المُسَجَّل دُخولها وأَنهِ أَيّ جَلسَة غَريبَة"
                   testid="account-sessions-link" />
              <Row onClick={() => setPwdOpen(true)} icon={Key} label="تَغيير كَلِمَة السِّرّ"
                   hint="تَحديث كَلِمَة السِّرّ الحالِيّة"
                   testid="account-change-password-link" />
            </Group>
          </>
        )}

        <SectionTitle>الدعم والمساعدة</SectionTitle>
        <Group>
          <Row to="/help" icon={Envelope} label="تواصل معنا" hint="الأسئلة الشائعة + البريد" testid="account-contact-link" />
          <Row to="/about" icon={Info} label="عن التطبيق" hint="الإصدار 1.0.0" testid="account-about-link" />
          <Row to="/account/agreement" icon={Scroll} label="الشُّروط والسِّياسات"
               hint="نَفس النَّصّ الَّذي وافَقتَ عَلَيه عِند التَّسجيل" testid="account-agreement-link" />
          {user && (
            <>
              <Row onClick={onLogout} icon={SignOut} label="تسجيل الخروج" testid="account-logout-btn" />
              <Row onClick={() => setDelOpen(true)} icon={Trash} label="حذف الحساب نهائياً" danger testid="account-delete-btn" />
            </>
          )}
        </Group>

        <div className="text-center mt-6 mb-2">
          <LexiqWordmark className="text-[20px]" testid="account-footer-lexiq" />
          <div className="text-[11.5px] text-[#8A8A8A] mt-0.5">
            الإصدار 1.0.0 · © {new Date().getFullYear()}
          </div>
        </div>
      </div>

      <ChangePasswordSheet open={pwdOpen} onClose={() => setPwdOpen(false)} />
      <DeleteAccountSheet open={delOpen} onClose={() => setDelOpen(false)} />
      {bioOpen && <BiometricSheet onClose={() => setBioOpen(false)} />}
      {sessionsOpen && <SessionsSheet onClose={() => setSessionsOpen(false)} />}
      <BarCardScanner open={renewOpen} onClose={() => setRenewOpen(false)} onComplete={handleRenewComplete} />
    </>
  );
}
